import { Button } from "@blueprintjs/core";
import { useFrontendVersion, useIsMobile, useJmWebConfig } from "app/jm";
import { UpdateAvailableDialog } from "app/UpdateAvailableDialog";
import { autoUpdateEnabledAtom } from "app/autoUpdatePreference";
import { useFrontendUpdateAvailable } from "app/useFrontendUpdateAvailable";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { type ReactNode, useEffect } from "react";

const AUTO_UPDATE_DELAY_MS = 5 * 60 * 1000;

type UpdateDialogMode = "automatic" | "manual";

export const updateAvailableAtom = atom(false);
export const updateDialogModeAtom = atom<UpdateDialogMode | null>(null);
export const autoUpdateDelayUntilAtom = atom(0);
export const delaySecondsRemainingAtom = atom<number | null>(null);

export type UpdateTriggerRenderProps = {
  label: string;
  onClick: () => void;
  updateAvailable: boolean;
};

type UpdateButtonProps = {
  isController?: boolean;
  renderTrigger?: (props: UpdateTriggerRenderProps) => ReactNode;
};

const UpdateController = () => {
  const { data: jmWebConfig } = useJmWebConfig();
  const { data: frontendVersion } = useFrontendVersion();
  const detectedUpdateAvailable = useFrontendUpdateAvailable({
    latestVersion: jmWebConfig?.latestVersion,
    frontendVersion,
  });
  const setUpdateAvailable = useSetAtom(updateAvailableAtom);
  const [updateDialogMode, setUpdateDialogMode] = useAtom(updateDialogModeAtom);
  const [autoUpdateDelayUntil, setAutoUpdateDelayUntil] = useAtom(
    autoUpdateDelayUntilAtom,
  );
  const setDelaySecondsRemaining = useSetAtom(delaySecondsRemainingAtom);
  const autoUpdateEnabled = useAtomValue(autoUpdateEnabledAtom);

  useEffect(() => {
    setUpdateAvailable(detectedUpdateAvailable);
  }, [detectedUpdateAvailable, setUpdateAvailable]);

  useEffect(() => {
    const delayUntil = autoUpdateEnabled ? autoUpdateDelayUntil : 0;
    if (!autoUpdateEnabled && autoUpdateDelayUntil !== 0) {
      setAutoUpdateDelayUntil(0);
    }

    const delayRemaining = delayUntil - Date.now();
    const secondsRemaining = Math.ceil(delayRemaining / 1000);
    setDelaySecondsRemaining(secondsRemaining > 0 ? secondsRemaining : null);

    const countdown =
      secondsRemaining > 0
        ? window.setInterval(() => {
            setDelaySecondsRemaining((current) =>
              current !== null && current > 1 ? current - 1 : null,
            );
          }, 1000)
        : null;

    let openDialogTimeout: number | null = null;
    if (
      detectedUpdateAvailable &&
      autoUpdateEnabled &&
      updateDialogMode === null
    ) {
      if (delayRemaining <= 0) {
        setUpdateDialogMode("automatic");
      } else {
        openDialogTimeout = window.setTimeout(() => {
          setUpdateDialogMode("automatic");
        }, delayRemaining);
      }
    }

    return () => {
      if (countdown !== null) window.clearInterval(countdown);
      if (openDialogTimeout !== null) window.clearTimeout(openDialogTimeout);
    };
  }, [
    autoUpdateDelayUntil,
    autoUpdateEnabled,
    detectedUpdateAvailable,
    setAutoUpdateDelayUntil,
    setDelaySecondsRemaining,
    setUpdateDialogMode,
    updateDialogMode,
  ]);

  return updateDialogMode ? (
    <UpdateAvailableDialog
      automatic={updateDialogMode === "automatic"}
      onClose={() => {
        if (updateDialogMode === "automatic") {
          setAutoUpdateDelayUntil(Date.now() + AUTO_UPDATE_DELAY_MS);
        }
        setUpdateDialogMode(null);
      }}
    />
  ) : null;
};

export const UpdateButton = ({
  isController = true,
  renderTrigger,
}: UpdateButtonProps = {}) => {
  const isMobile = useIsMobile();
  const updateAvailable = useAtomValue(updateAvailableAtom);
  const delaySecondsRemaining = useAtomValue(delaySecondsRemainingAtom);
  const setUpdateDialogMode = useSetAtom(updateDialogModeAtom);
  const delayCountdown =
    delaySecondsRemaining === null
      ? null
      : `${Math.floor(delaySecondsRemaining / 60)}:${String(
          delaySecondsRemaining % 60,
        ).padStart(2, "0")}`;
  const label = delayCountdown
    ? `Update Now (${delayCountdown})`
    : isMobile
      ? "Update"
      : "Update Available";
  const triggerProps: UpdateTriggerRenderProps = {
    label,
    onClick: delayCountdown
      ? () => setTimeout(() => window.location.reload(), 250)
      : () => setUpdateDialogMode("manual"),
    updateAvailable,
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <Button
          text={label}
          intent="success"
          textClassName={isMobile && delayCountdown ? "text-xs" : ""}
          className="!min-w-24 !max-h-[30px]"
          hidden={!updateAvailable}
          onClick={triggerProps.onClick}
        />
      )}
      {isController && <UpdateController />}
    </>
  );
};
